import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Crown, UserPlus, CheckCircle2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const nav = useNavigate();
  const [state, setState] = useState("checking"); // checking | valid | invalid | done
  const [invite, setInvite] = useState(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); setError("No invite token provided."); return; }
    api.get(`/invite/${token}`)
      .then((r) => { setInvite(r.data); setState("valid"); })
      .catch((e) => { setState("invalid"); setError(formatApiError(e.response?.data?.detail) || "Invalid invite"); });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 12 || !/[A-Z]/.test(pw) || !/[a-z]/.test(pw) || !/[0-9]/.test(pw))
      return setError("Password must be at least 12 characters with an uppercase letter, a lowercase letter, and a number.");
    if (pw !== pw2) return setError("Passwords don't match.");
    setError(""); setBusy(true);
    try {
      await api.post(`/invite/${token}/accept`, { password: pw });
      setState("done");
      toast.success("Account activated — you can sign in now");
      setTimeout(() => nav("/login"), 1800);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not accept invite");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm" data-testid="accept-invite-card">
        <Link to="/" className="flex items-center gap-2 mb-8"><Crown className="w-6 h-6 text-accent" /><span className="font-display font-extrabold text-lg tracking-tight">GALAHADJI</span></Link>

        {state === "checking" && <div className="text-muted-foreground text-sm">Checking your invite…</div>}

        {state === "invalid" && (
          <div data-testid="invite-invalid">
            <h1 className="font-display text-2xl font-bold tracking-tight">Invite unavailable</h1>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
            <Link to="/login" className="inline-block mt-6 text-sm text-foreground underline underline-offset-4">Go to login</Link>
          </div>
        )}

        {state === "done" && (
          <div className="text-center" data-testid="invite-done">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold tracking-tight mt-3">You're all set!</h1>
            <p className="text-sm text-muted-foreground mt-2">Redirecting you to sign in…</p>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit} data-testid="invite-form">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2"><UserPlus className="w-3.5 h-3.5" /> Team Invite</div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Set your password</h1>
            <p className="text-sm text-muted-foreground mt-1">Joining as <span className="font-medium text-foreground">{invite?.email}</span> · {invite?.role}</p>

            {error && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-4 py-3 mt-4" data-testid="invite-error">{error}</div>}

            <div className="space-y-4 mt-6">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">New Password</Label>
                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="invite-password" required />
                <p className="text-xs text-muted-foreground">At least 12 characters with an uppercase letter, a lowercase letter, and a number.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Confirm Password</Label>
                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="invite-password-confirm" required />
              </div>
            </div>
            <Button type="submit" disabled={busy} data-testid="invite-submit" className="w-full mt-6 h-11">{busy ? "Activating…" : "Activate Account"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
